// Document Vault shared types and constants (TASK-VAULT-001, P0).
// The server (netlify/functions/documents.mts + src/app/api/documents/route.ts)
// keeps its own copies of the cap/limits; keep these in sync if they change.

export type VaultCategory = 'identity' | 'competency' | 'stcw' | 'medical' | 'travel' | 'other';

export interface VaultCategoryMeta {
  key: VaultCategory;
  label: string;
  hint: string;
}

export const VAULT_CATEGORIES: VaultCategoryMeta[] = [
  { key: 'identity', label: 'Identity', hint: 'Passport, CDC, national ID' },
  { key: 'competency', label: 'Competency', hint: 'CoC, CoP, endorsements' },
  { key: 'stcw', label: 'STCW', hint: 'Basic safety, advanced courses' },
  { key: 'medical', label: 'Medical', hint: 'Medical fitness, vaccination' },
  { key: 'travel', label: 'Travel / Visa', hint: 'Visas, seaman book, letters' },
  { key: 'other', label: 'Other', hint: 'Anything else' },
];

export const VAULT_CATEGORY_LABEL: Record<VaultCategory, string> = VAULT_CATEGORIES.reduce(
  (acc, c) => {
    acc[c.key] = c.label;
    return acc;
  },
  {} as Record<VaultCategory, string>,
);

export function coerceVaultCategory(value: string | null | undefined): VaultCategory {
  return VAULT_CATEGORIES.some((c) => c.key === value) ? (value as VaultCategory) : 'other';
}

// Free-tier cap (spec §10). Paid upgrade lifts this in a later billing phase.
export const FREE_DOCUMENT_CAP = 30;

// Upload limits (mirrors the server). Images are compressed client-side first.
// Capped at 5 MB to stay under the Netlify Function request-body limit (the
// live /api/documents path runs as a synchronous function), matching the
// proven support-attachments cap. In practice images land far below this; the
// cap mainly guards large PDFs (which we can't recompress without a library).
export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;
export const MAX_DOCUMENT_MB = 5;

// Target output sizes for client-side image compression. Identity documents
// (passport, CDC, national ID) keep more detail/resolution so fine print and
// the photo stay legible; everything else is squeezed harder to protect the
// shared (free-tier) storage budget.
export const IDENTITY_TARGET_BYTES = 500 * 1024;
export const DEFAULT_TARGET_BYTES = 200 * 1024;

export interface CompressionTarget {
  targetBytes: number;
  maxEdge: number;
}

export function compressionTargetFor(category: VaultCategory): CompressionTarget {
  return category === 'identity'
    ? { targetBytes: IDENTITY_TARGET_BYTES, maxEdge: 2400 }
    : { targetBytes: DEFAULT_TARGET_BYTES, maxEdge: 1600 };
}
export const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

export type VaultStorageKind = 'supabase' | 'drive';

export interface VaultDocument {
  id: string;
  user_id: string;
  category: VaultCategory;
  title: string;
  linked_record_id: string | null;
  linked_certificate_id: string | null;
  file_name: string;
  file_path: string | null;        // null for Drive-stored (encrypted) docs
  file_type: string | null;
  file_size: number | null;
  expiry_date: string | null;
  created_at: string;
  updated_at: string;
  // Drive-backed storage (TASK-VAULT-CLOUD-001):
  storage_kind?: VaultStorageKind; // defaults to 'supabase' for legacy rows
  drive_file_id?: string | null;   // Drive appDataFolder file id
  enc_iv?: string | null;          // base64 AES-GCM IV
  signedUrl?: string;              // legacy Supabase docs only (client-attached)
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
